import * as swcHelpers from '@swc/helpers';
import * as React from 'react';
import { useLocalStorage } from '@grandvisionhq/utils';
import { cartIdVar } from '../utils/cart-id-var.js';
import { cartCountVar } from '../utils/cart-count-var.js';

const CART_STORAGE_KEY = 'stored_cart';
const initialState = {
    cartId: null,
    cartHash: null,
    cartCount: 0
};
const useCartStorage = ()=>{
    const [data, setData] = useLocalStorage(CART_STORAGE_KEY, initialState);
    React.useDebugValue(data);
    /**
   *  Gql reactive variable is used to always keep latest cartId available
   *  and can be shared with components
   *  Problem :
   *  When component/hook consumes useCartStorage and indirectly updated cartId then latest cartId
   *  is not returned to consumer as it is not direct source of change.
   *  For e.g
   *  createOrder saga is called from StepPaymentContainer and if order fails replicateOrder saga is called.
   *  replicateOrder saga dispatches 'cartChange' event which results in loading new cart using useGetCart hook.
   *  useGetCart hook updates cartId in localstorage via useCartStorage but updated cartId is only available to useGetCart hook.
   *  All other consumers of useCartStorage will have old cartId as there was no re-render.
   *
   *  Solution:
   *  `cartIdVar` will always have latest cart Id, It can be accessed getter signature or via hook
   *   `
   *   import { useReactiveVar } from '@grandvisionhq/graphql'
   *   const cartId = useReactiveVar(cartIdVar)
   *   `
   *   OR
   *   `cartIdVar()`
   *
   */ React.useEffect(()=>{
        cartIdVar(data.cartId);
    }, [
        data.cartId
    ]);
    const setCartId = React.useCallback((cartId)=>{
        cartIdVar(cartId);
        setData((state)=>swcHelpers.objectSpread({}, state, {
                cartId
            })
        );
    }, [
        setData
    ]);
    const setCartHash = React.useCallback((cartHash)=>{
        setData((state)=>swcHelpers.objectSpread({}, state, {
                cartHash
            })
        );
    }, [
        setData
    ]);
    const setCartCount = React.useCallback((newCount)=>{
        if (cartCountVar() !== newCount) {
            cartCountVar(newCount);
        }
        return setData((state)=>{
            if (state.cartCount === newCount) {
                return state;
            }
            return swcHelpers.objectSpread({}, state, {
                cartCount: newCount
            });
        });
    }, [
        setData
    ]);
    const reset = ()=>{
        setData(initialState);
        cartCountVar(0);
    };
    return [
        data,
        {
            setCartId,
            setCartHash,
            setCartCount,
            reset
        }
    ];
};

export { CART_STORAGE_KEY, useCartStorage };
//# sourceMappingURL=use-cart-storage.js.map
